import type { DispatchJob } from '../types/dispatch';
import { instantTenantDay, toCalendarDateString } from './calendar-date';

/**
 * Which cards the dispatch week board draws on a given day, and what each one
 * says about its phase clock (API 0403).
 *
 * ─── THE TWO PHASES ─────────────────────────────────────────────────────────
 *
 *   run    PLANNED by scheduled_date,  ACTUAL run_started_at  -> run_ended_at
 *   wash   PLANNED by date_time_wash,  ACTUAL wash_started_at -> wash_ended_at
 *
 * Same vocabulary as the API's lib/job-phase.ts. NOT the field dashboard's
 * `day_phase`, which answers a different question from the planned instants.
 *
 * ─── WHERE A PHASE'S CARD APPEARS ───────────────────────────────────────────
 *
 * Not started: its scheduled day only, exactly as the board always did —
 * including the old rule that a wash sharing the run's day draws no card of
 * its own.
 *
 * Resumed (API 0534): a phase ended and later resumed has several STINTS in
 * `phase_stints`. Each stint is placed by the rule below, the gap days between
 * them draw nothing, and "Day N" counts days worked — the API's lib/board-day.ts,
 * restated here because the board has to draw it. A phase with no recorded
 * stints reads its span columns as one stint, which is the rule before 0534.
 *
 * Started: every day from the start day to (end day ?? tenant_today). A run
 * that began Monday and is still going Wednesday is on the board all three
 * days, because the crew is on that location all three days. Day 1 is the real
 * card; days 2+ are CONTINUATIONS — labelled "Day N", dimmed, and not
 * draggable, because dragging one would move the scheduled date of a phase that
 * has already started, and the scheduled date never moves on account of the
 * carry-over.
 *
 * ─── "TODAY" IS THE API'S, NEVER THE BROWSER'S ──────────────────────────────
 *
 * `tenant_today` comes back beside the job list, resolved in the tenant's zone.
 * A browser after 18:00 in Alberta reads UTC's tomorrow, so a
 * device-derived today would stretch every open phase one day too far. When
 * it is absent, nothing is carried: a started, unended phase shows its start
 * day only.
 *
 * The start and end are INSTANTS, and the day they fall on is resolved in the
 * tenant's zone for the same reason. An unusable zone places nothing — the
 * phase falls back to its scheduled day rather than a guessed one.
 *
 * ─── THE LEGACY RUN START ───────────────────────────────────────────────────
 *
 * A job started before 0403 has started_at and no phase columns. The API
 * derives the run start for those rows (`run_started_at_effective`) and FLAGS
 * the derivation (`run_start_is_carried_over`). This file reads those two and
 * does not restate the rule: a second copy of it here is the "two answers to
 * one question" CLAUDE.md forbids. If the API omits them, the run is simply
 * not started.
 */

export type CardPhase = 'run' | 'wash';

/** What one phase's clock reads, as the board displays it. */
export interface PhaseClock {
  startedAt: string | null;
  endedAt: string | null;
  /** The start is the API's inference from started_at, not a recorded tap. */
  startCarriedOver: boolean;
}

export interface PhaseCard {
  job: DispatchJob;
  cardType: CardPhase;
  /** The column this card sits in, 'yyyy-MM-dd'. */
  day: string;
  /** 1 on the start (or scheduled) day, N on the Nth carried-over day. */
  dayNumber: number;
  isContinuation: boolean;
  dragId: string;
  clock: PhaseClock;
  /**
   * Whether this is the card that carries the Start / End control. One per
   * phase: the scheduled card before the start, the LAST carried-over day while
   * it runs (today's, when tenant_today is known), and none once it has ended.
   */
  showsControls: boolean;
  /**
   * Whether this job draws a wash card of its own. When it does, each card
   * shows only the hands working its phase (lib/dispatch-crew-phase.ts); when
   * it does not, the one card is the whole job and shows everyone.
   */
  crewSplit: boolean;
}

/** The draggable payload of a job card. Read from `active.data.current`. */
export interface JobCardDragData {
  type: 'job';
  job: DispatchJob;
  jobId: string;
  cardType: CardPhase;
  /** Null for a card with no day column — the Pending and Status boards. */
  day: string | null;
}

export const phaseDragId = (jobId: string, phase: CardPhase, day: string): string =>
  `${jobId}__${phase}__${day}`;

/** The 'yyyy-MM-dd' prefix of a stored date, as the board has always read it. */
function datePrefix(value: string | null | undefined): string | null {
  if (!value) return null;
  const prefix = String(value).split(/[T ]/)[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(prefix) ? prefix : null;
}

/** Whole days from `a` to `b`, both 'yyyy-MM-dd'. Arithmetic, no clock. */
function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

export function phaseClock(job: DispatchJob, phase: CardPhase): PhaseClock {
  if (phase === 'wash') {
    return {
      startedAt: job.wash_started_at ?? null,
      endedAt: job.wash_ended_at ?? null,
      startCarriedOver: false,
    };
  }
  const recorded = job.run_started_at ?? null;
  return {
    startedAt: recorded ?? job.run_started_at_effective ?? null,
    endedAt: job.run_ended_at ?? null,
    startCarriedOver: recorded == null && job.run_start_is_carried_over === true,
  };
}

/**
 * Whether the job's PLANNED wash falls on a day of its own — the condition the
 * board has always drawn a separate wash card by.
 *
 * Exported because the crew screens ask the same question away from the board,
 * where there is no BoardContext to evaluate `jobDrawsWashCard`'s other clause
 * with: `/jobs/:id` carries the job's own columns and neither `tenant_today`
 * nor the tenant zone. This is the narrower of the two tests — it does not see
 * a wash STARTED with no planned date — and the board keeps using the wider
 * one. No production job is in that state (0 estate-wide, checked 2026-09-17),
 * and the disagreement is safe in the direction it falls: the screen offers no
 * selector, the hand is saved with no phase, and NULL is every phase.
 */
export function jobHasSeparateWashDay(job: Pick<DispatchJob, 'scheduled_date' | 'date_time_wash'>): boolean {
  const wash = datePrefix(job.date_time_wash);
  // A wash on the run's own day has never drawn a card of its own.
  return wash != null && wash !== datePrefix(job.scheduled_date);
}

function scheduledDay(job: DispatchJob, phase: CardPhase): string | null {
  if (phase === 'run') return datePrefix(job.scheduled_date);
  return jobHasSeparateWashDay(job) ? datePrefix(job.date_time_wash) : null;
}

export interface BoardContext {
  tenantToday: string | null | undefined;
  timeZone: string | null | undefined;
}

/**
 * Whether the job draws a wash card on some day — the same two conditions
 * phaseCardOnDay places a wash card by: a start instant that resolves to a day,
 * or a planned wash on a day other than the run's.
 */
export function jobDrawsWashCard(job: DispatchJob, ctx: BoardContext): boolean {
  return instantTenantDay(phaseClock(job, 'wash').startedAt, ctx.timeZone) != null
    || scheduledDay(job, 'wash') != null;
}

function makeCard(
  job: DispatchJob,
  phase: CardPhase,
  day: string,
  dayNumber: number,
  showsControls: boolean,
  ctx: BoardContext,
): PhaseCard {
  return {
    job,
    cardType: phase,
    day,
    dayNumber,
    isContinuation: dayNumber > 1,
    dragId: phaseDragId(job.id, phase, day),
    clock: phaseClock(job, phase),
    showsControls,
    crewSplit: jobDrawsWashCard(job, ctx),
  };
}

/** The card for one phase of one job on `day`, or null if it is not there. */
export function phaseCardOnDay(
  job: DispatchJob,
  phase: CardPhase,
  day: string,
  ctx: BoardContext,
): PhaseCard | null {
  const clock = phaseClock(job, phase);
  const build = (dayNumber: number, showsControls: boolean): PhaseCard =>
    makeCard(job, phase, day, dayNumber, showsControls, ctx);

  const today = datePrefix(ctx.tenantToday ?? null);
  const stints = phaseStints(job, phase)
    .map((s) => stintDays(s, ctx.timeZone, today))
    .filter((s): s is StintDays => s != null);

  if (!stints.length) {
    // Not started — or started at an instant that cannot be placed, which is
    // shown where it was planned rather than on a guessed day.
    return scheduledDay(job, phase) === day ? build(1, clock.endedAt == null) : null;
  }

  // A gap day between two stints is in none of them, so it draws nothing.
  const hit = stints.find((s) => day >= s.startDay && day <= s.lastDay);
  if (!hit) return null;

  // "Day N" counts days WORKED, as the API's board-day.ts does: the first day
  // back after a gap is the day after the last day worked, not the calendar
  // distance from the first start. A day two stints share counts once.
  const worked = new Set<string>();
  for (const s of stints) {
    if (s.startDay > day) continue;
    for (const d of daysFromTo(s.startDay, s.lastDay < day ? s.lastDay : day)) worked.add(d);
  }
  const dayNumber = stints.length === 1 ? daysBetween(hit.startDay, day) + 1 : worked.size;
  return build(dayNumber, hit.open && day === hit.lastDay);
}

/**
 * A phase's stints: the recorded ones (API 0534), else the span as one stint,
 * else none. The fallback is the board's rule before stints existed, so a job
 * with no stint rows — the legacy carried-over run among them — places exactly
 * as it did.
 */
function phaseStints(job: DispatchJob, phase: CardPhase): { startedAt: string | null; endedAt: string | null }[] {
  const recorded = (job.phase_stints?.[phase] ?? []).filter((s) => s?.started_at != null);
  if (recorded.length) {
    return recorded
      .map((s) => ({ startedAt: s.started_at, endedAt: s.ended_at ?? null }))
      .sort((a, b) => (a.startedAt < b.startedAt ? -1 : a.startedAt > b.startedAt ? 1 : 0));
  }
  const clock = phaseClock(job, phase);
  return clock.startedAt == null ? [] : [clock];
}

interface StintDays {
  startDay: string;
  lastDay: string;
  /** Still running — its last day is tenant_today (or its start day without one). */
  open: boolean;
}

/** A stint's board days in the tenant zone, or null when its start cannot be placed. */
function stintDays(
  stint: { startedAt: string | null; endedAt: string | null },
  timeZone: string | null | undefined,
  today: string | null,
): StintDays | null {
  const startDay = instantTenantDay(stint.startedAt, timeZone);
  if (!startDay) return null;
  const endDay = instantTenantDay(stint.endedAt, timeZone);
  let lastDay = endDay ?? today ?? startDay;
  if (lastDay < startDay) lastDay = startDay;
  return { startDay, lastDay, open: stint.endedAt == null };
}

/** Every 'yyyy-MM-dd' from `a` to `b` inclusive. Arithmetic, no clock. */
function daysFromTo(a: string, b: string): string[] {
  const out: string[] = [];
  const [y, m, d] = a.split('-').map(Number);
  for (let i = 0; i <= daysBetween(a, b) && i < 1000; i++) {
    out.push(toCalendarDateString(new Date(y, m - 1, d + i)));
  }
  return out;
}

/**
 * Every card for `day`, run before wash per job. Completed and cancelled jobs
 * are not on the day columns — they belong to the ticket and invoice queues.
 */
export function cardsForDay(jobs: DispatchJob[], day: string, ctx: BoardContext): PhaseCard[] {
  const cards: PhaseCard[] = [];
  for (const job of jobs) {
    if (job.status === 'completed' || job.status === 'cancelled') continue;
    for (const phase of ['run', 'wash'] as const) {
      const card = phaseCardOnDay(job, phase, day, ctx);
      if (card) cards.push(card);
    }
  }
  return cards;
}

/**
 * One card per job across `days`, for the two views that bucket a job rather
 * than a phase: the month calendar (one day per cell) and the Month tab (seven
 * days per week column). Placement is the board's — the rule above, not a copy
 * of it — and each view keeps the job set it always had.
 *
 * ─── WHY CLOSED JOBS STAY ON THEIR PLANNED DAYS ─────────────────────────────
 *
 * Unlike the week board, these views have always shown completed and cancelled
 * jobs. The API carries `started_at` over as the run start on EVERY pre-0403
 * job, and a job closed before 0403 has no run end either, so running a closed
 * job through the carry-over would draw it from its start to tenant_today —
 * months of cells for work that finished long ago. A closed job is placed where
 * it was planned, exactly as these views always placed it, and carries nothing.
 *
 * ─── WHICH CARD STANDS FOR THE JOB ──────────────────────────────────────────
 *
 * These views drew a job once per bucket, and still do. When a bucket holds
 * several of a job's cards (a run carried into its wash day, or a week column
 * holding a whole run), the first real card wins over a continuation, so a
 * phase that starts in the bucket is what the job shows; failing that, the
 * earliest continuation, run before wash.
 */
export function jobCardsForDays(jobs: DispatchJob[], days: string[], ctx: BoardContext): PhaseCard[] {
  const chosen = new Map<string, PhaseCard>();
  for (const day of days) {
    const cards: PhaseCard[] = [];
    for (const job of jobs) {
      const closed = job.status === 'completed' || job.status === 'cancelled';
      for (const phase of ['run', 'wash'] as const) {
        if (closed) {
          if (scheduledDay(job, phase) === day) cards.push(makeCard(job, phase, day, 1, false, ctx));
        } else {
          const card = phaseCardOnDay(job, phase, day, ctx);
          if (card) cards.push(card);
        }
      }
    }
    for (const card of cards) {
      const held = chosen.get(card.job.id);
      if (!held || (held.isContinuation && !card.isContinuation)) chosen.set(card.job.id, card);
    }
  }
  // Job order, as the views always listed them.
  return jobs.flatMap(job => {
    const card = chosen.get(job.id);
    return card ? [card] : [];
  });
}

